// src/pages/Work/UseCases/DesktopApp/DesktopApp.js
import React from 'react';
import '../DevelopmentTools/DevelopmentTools.css';

function DesktopApp({ onNavigate, isFirst, isLast }) {
  return (
    <div className="use-case-container">
      <div className="use-case-header">
        <h3 className="use-case-title">
          Desktop App / The leading world logistics companies
        </h3>
        <div className="navigation-buttons">
          <button
            onClick={() => onNavigate('prev')}
            disabled={isFirst}
            className="nav-button"
          >
            &lt;
          </button>
          <button
            onClick={() => onNavigate('next')}
            disabled={isLast}
            className="nav-button"
          >
            &gt;
          </button>
        </div>
      </div>

      {/* Overview */}
      <div id="desktopapp-overview" className="use-case-section">
        <h2 className="section-title">Overview</h2>
        <div className="overview-content">
          <p>
            I have <strong>enhanced logistics desktop applications</strong> by focusing on:
          </p>
          <ul className="overview-list">
            <li>
              <strong>Usability:</strong> Crafting intuitive navigation and clear instructions for effortless user interactions.
            </li>
            <li>
              <strong>Aesthetics:</strong> Designing visually appealing interfaces to make interactions engaging.
            </li>
            <li>
              <strong>Consistency:</strong> Maintaining uniform design elements to foster familiarity and ease of use.
            </li>
            <li>
              <strong>Performance Optimization:</strong> Collaborating with development teams to ensure quick response times and seamless interactions.
            </li>
          </ul>
          <p>
            This approach ensures that logistics applications are functional, satisfying, and efficient for users.
          </p>
        </div>
      </div>

      {/* Specification */}
      <div id="desktopapp-specification" className="use-case-section">
        <h2 className="section-title">Specification</h2>
        <div className="specification-content">
          <h1 className="tool-name">The Leading World Logistics Company</h1>
          <div className="spec-details">
            <div className="spec-row">
              <div className="spec-label">Duration:</div>
              <div>2 months</div>
            </div>
            <div className="spec-row">
              <div className="spec-label">Tools Used:</div>
              <div>Figma, Confluence</div>
            </div>
          </div>
          <div>
            <h3 className="highlight-blue" style={{ fontWeight: 'bold' }}>Goal</h3>
            <p className="goal-text">
              Enhance productivity and user satisfaction within a logistics desktop application.
            </p>
          </div>
          {/* Specification Image */}
          <img
            src={require("../../../../assets/use-case-specification.png")}
            alt="Use Case Specification Illustration"
            className="specification-image"
            style={{ 
              width: '100%', 
              maxWidth: '600px', 
              marginTop: '2rem',
              borderRadius: '4px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
            }}
          />
        </div>
      </div>

      {/* Problem Challenge */}
      <div id="desktopapp-problem" className="use-case-section">
        <h2 className="section-title">
          <span className="highlight-blue">Problem</span> Challenge
        </h2>
        <div className="problem-content">
          <p>
            In my role as a UX designer for <strong>a leading global logistics company</strong>, I faced the challenge of <strong>limited direct access to end-users</strong>. To address this, I developed a project grounded in established <strong>usability principles</strong> and <strong>general UX guidelines</strong>. This approach ensured that the application was user-friendly, efficient, and aligned with industry best practices, even without direct user input.
          </p>

          <div className="impact-section">
            <h3 className="highlight-blue">My impact</h3>
            <p>In this project, I concentrated on:</p>
            <ul className="impact-list">
              <li>
                <strong>Enhancing the Existing Interface:</strong> I conducted heuristic evaluations, applying Jakob Nielsen's usability principles to improve the tool's interface and functionality.
              </li>
              <li>
                <strong>Redesigning for Brand Consistency:</strong> I revamped the tool's interface to align with established brand guidelines, ensuring visual harmony and better usability.
              </li>
            </ul>
            <p>
              By implementing these strategies, I ensured the design adhered to <strong>recognized usability standards</strong>, resulting in a <strong>more intuitive and efficient</strong> user experience.
            </p>
          </div>
        </div>
      </div>

      {/* The Process Approach */}
      <div id="desktopapp-process" className="use-case-section">
        <h2 className="section-title">
          <span className="highlight-blue">The Process</span> Approach
        </h2>
        <div className="process-content">
          <p>
            In this project, I emphasized design principles and applied my expertise to enhance the user experience.
          </p>

          <h3 className="highlight-blue">Heuristic Evaluation for Enhanced Application Usability</h3>
          <p>
            I conducted <strong>heuristic evaluations</strong> to improve application usability by assessing the user interface against <strong>Jakob Nielsen's 10 usability heuristics</strong>:
          </p>

          <h3 className="highlight-blue">1. Visibility of System Status</h3>
          <p>
            Ensure users receive <strong>immediate feedback</strong> on their actions by implementing clear mechanisms such as <strong>visual cues</strong>.
          </p>

          <h3 className="highlight-blue">2. Match Between System and the Real World</h3>
          <p>
            Use <strong>intuitive designs</strong> that mirrored real-world interactions, making interactions making the system <strong>more natural and familiar</strong>.
          </p>

          <h3 className="highlight-blue">3. User Control and Freedom</h3>
          <p>
            Enable users to <strong>undo mistakes</strong>, <strong>navigate freely</strong>, and <strong>recover lost work</strong>, reducing frustration and enhancing confidence.
          </p>

          <h3 className="highlight-blue">4. Consistency and Standards</h3>
          <p>
            Maintained <strong>uniform design</strong>, <strong>terminology</strong>, and platform-specific <strong>guidelines</strong> to make the system predictable and user-friendly across different screens.
          </p>

          <h3 className="highlight-blue">5. Error Prevention</h3>
          <p>
            Proactively reduce errors by <strong>minimizing costly mistakes</strong> and enhancing user confidence through preventive design measures.
          </p>

          <h3 className="highlight-blue">6. Recognition Rather Than Recall</h3>
          <p>
            <strong>Streamline navigation</strong> by making options <strong>visible and easy to recognize</strong>, helping users rely on recognition rather than memory.
          </p>

          <h3 className="highlight-blue">7. Flexibility and Efficiency of Use</h3>
          <p>
            Provide <strong>keyboard shortcuts</strong>, <strong>customization</strong>, and smart <strong>workflows</strong> to accommodate both novice and expert users
          </p>

          <h3 className="highlight-blue">8. Aesthetic and Minimalist Design</h3>
          <p>
            <strong>Simplify the UI</strong> by removing clutter, maintaining a clear hierarchy, and keeping the <strong>design clean</strong>, uncluttered, and focused on essentials.
          </p>

          <h3 className="highlight-blue">9. Help Users Recognize, Diagnose, and Recover from Errors</h3>
          <p>
            Design <strong>clear, actionable error messages</strong> that help users quickly identify and resolve issues.
          </p>

          <h3 className="highlight-blue">10. Help and Documentation</h3>
          <p>
            Ensure users can easily access <strong>help resources</strong> and <strong>searchable documentation</strong> without disrupting their workflow.
          </p>
        </div>
      </div>

      {/* Solution Ideation */}
      <div id="desktopapp-solution" className="use-case-section">
        <h2 className="section-title">
          <span className="highlight-blue" style={{ fontWeight: 'bold' }}>Solution</span> Ideation
        </h2>
        <div className="solution-content">
          {/* Solution Image */}
          <img
            src={require("../../../../assets/solution-ideation.png")}
            alt="Solution Ideation Illustration"
            className="solution-image"
            style={{ 
              width: '100%', 
              maxWidth: '600px', 
              marginBottom: '1.5rem',
              borderRadius: '4px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
            }}
          />
          
          <p>
            By implementing these solutions, the application became more intuitive, efficient, and user-friendly, aligning with industry best practices.
          </p>

          <h3 className="highlight-blue" style={{ fontWeight: 'bold' }}>Applying Jakob Nielsen's 10 usability heuristics led to</h3>
          <ul className="solution-list">
            <li>
              <span className="highlight-blue" style={{ fontWeight: 'bold' }}>Improved usability</span> – Clear feedback, intuitive navigation, and error prevention reduced friction in user interactions.
            </li>
            <li>
              <span className="highlight-blue" style={{ fontWeight: 'bold' }}>Enhanced productivity</span> – Streamlined workflows, keyboard shortcuts, and smart defaults helped users complete tasks faster.
            </li>
            <li>
              <span className="highlight-blue" style={{ fontWeight: 'bold' }}>Increased user satisfaction</span> – A clean, consistent, and responsive design improved the overall experience.
            </li>
            <li>
              <span className="highlight-blue" style={{ fontWeight: 'bold' }}>Reduced errors</span> – Input validation, actionable error messages, and recovery options minimized mistakes and frustration.
            </li>
          </ul>

          <h3 className="highlight-blue" style={{ fontWeight: 'bold' }}>Decluttered the interface by:</h3>
          <ul className="solution-list">
            <li>Removing outdated elements</li>
            <li>Introducing a clean, modern, and responsive layout</li>
            <li>Working with technical writers to ensure concise and user-friendly UI text</li>
          </ul>

          <p>
            This process reinforced the value of structured UX evaluation and its direct impact on user engagement and system efficiency.
          </p>
        </div>
      </div>

      {/* Conclusion */}
      <div id="desktopapp-conclusion" className="use-case-section">
        <h2 className="section-title">
          <span className="highlight-blue" style={{ fontWeight: 'bold' }}>Conclusion</span>
        </h2>
        <div className="conclusion-content">
          <h3 style={{ color: '#aaa', marginBottom: '1rem' }}>Achieving Usability Goals Without Direct User Feedback</h3>
          
          <p>
            Applying <strong>Jakob Nielsen's 10 usability heuristics</strong> gave me a deeper understanding of what makes a great user experience. However, it was a challenge to do this <span className="highlight-blue" style={{ fontWeight: 'bold' }}>without direct access to real users</span>.
          </p>

          <p>Here's what I took away from the process:</p>
          
          <ul className="solution-list">
            <li>
              <span className="highlight-blue" style={{ fontWeight: 'bold' }}>People need clear feedback</span>. When users know what's happening—whether it's a loading bar, a success message, or an error prompt—they feel more confident and in control.
            </li>
            <li>
              <span className="highlight-blue" style={{ fontWeight: 'bold' }}>Mistakes should be easy to fix</span> <strong>(or avoid)</strong>. Giving users an "undo" button or a quick warning before deleting something important prevents frustration.
            </li>
            <li>
              <span className="highlight-blue" style={{ fontWeight: 'bold' }}>Less is more</span>. A clean, simple design without unnecessary clutter makes it easier for users to focus and get things done.
            </li>
            <li>
              <span className="highlight-blue" style={{ fontWeight: 'bold' }}>Everyone works differently</span>. Some users love shortcuts and power features, while others prefer step-by-step guidance. A great UI should cater to both.
            </li>
            <li>
              <span className="highlight-blue" style={{ fontWeight: 'bold' }}>Help should be easy to find</span>. Nobody wants to dig through a manual—inline hints, tooltips, and smart error messages make all the difference.
            </li>
          </ul>

          <p>
            Doing this <strong>without real user feedback</strong> meant <strong>relying on best practices</strong> and <strong>usability principles</strong> rather than direct observations. While <span className="highlight-blue" style={{ fontWeight: 'bold' }}>heuristics are a great framework</span>, real users bring unexpected insights that can't be predicted. Despite this challenge, the goal was achieved :) the implemented improvements help users.
          </p>
        </div>
      </div>
    </div>
  );
}

export default DesktopApp;